import React, { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { useSearchContext } from './SearchContext';
import { useLocation, useNavigate } from 'react-router-dom';

function ViewDocument() {
    const { accessionNumber } = useParams();
    const [documentHtml, setDocumentHtml] = useState(null);
    const [error, setError] = useState(null);
    const { baseUrl } = useSearchContext();
    const apiUrl = `${baseUrl}/brockden.php`;

    const containerRef = useRef(null);
    const [leftWidth, setLeftWidth] = useState(342);
    const isDragging = useRef(false);
    const { setDocTitle, setDocAuthor, isPrintView, docTitle, setIsDocumentLoaded, USE_HASH_ROUTER } = useSearchContext();
    const location = useLocation();
    const searchParams = new URLSearchParams(location.search);
    const keyword = searchParams.get('keyword') || '';
    const entireText = searchParams.get('entireText') || '';

    const [sectionKeywordMatches, setSectionKeywordMatches] = useState([]);
    const [parsedToc, setParsedToc] = useState([]);

    const [totalMatches, setTotalMatches] = useState(0);
    const [highlightedElements, setHighlightedElements] = useState([]);
    const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
    const MIN_WIDTH = 0;
    const MAX_WIDTH = 600;
    const containerWidth = containerRef.current?.offsetWidth || 0;
    const isRightCollapsed = leftWidth >= containerWidth - MIN_WIDTH;

    const navigate = useNavigate();

    const handleClearHits = () => {
        if (USE_HASH_ROUTER) {
            const [hashPath] = window.location.hash.split('?');
            window.location.hash = hashPath;
            window.location.reload(); // only for HashRouter
        } else {
            // For BrowserRouter, just navigate to the same path without query params
            const basePath = location.pathname;
            navigate(basePath, { replace: true });
        }
    };

    useEffect(() => {
        const mainEl = document.querySelector('main.main-content');
        if (mainEl) {
            mainEl.classList.add('view-document-style');
        }

        return () => {
            if (mainEl) {
                mainEl.classList.remove('view-document-style');
            }
        };
    }, []);

    useEffect(() => {
        if (!accessionNumber) {
            setError("Invalid document ID.");
            return;
        }

        axios
            .post(apiUrl, {
                documentInfo: { AccessionNumber: accessionNumber },
            })
            .then((response) => {
                setDocumentHtml(response.data);
                setIsDocumentLoaded(true);
            })
            .catch((err) => {
                setError(err.message);
                setIsDocumentLoaded(false);
            });
    }, [apiUrl, accessionNumber, setIsDocumentLoaded]);

    useEffect(() => {
        if (!accessionNumber) return;

        axios
            .post(apiUrl, {
                documentInfo: {
                    AccessionNumber: accessionNumber,
                    Toc: true
                }
            })
            .then((response) => {
                const { html, title, author } = response.data;
                setDocTitle(title);
                setDocAuthor(author);

                // Parse TOC into structure
                const parser = new DOMParser();
                const doc = parser.parseFromString(html, "text/html");
                const links = Array.from(doc.querySelectorAll("a[href^='#']"));
                const tocData = links.map(link => ({
                    href: link.getAttribute("href"),
                    title: link.textContent
                }));
                setParsedToc(tocData);
            })
            .catch((err) => {
                console.error("Failed to fetch TOC:", err);
            });
    }, [accessionNumber, apiUrl, setDocTitle, setDocAuthor]);


    useEffect(() => {
        const hasSearchTerm = keyword || entireText;
        if (!accessionNumber || !hasSearchTerm) return;

        axios
            .post(apiUrl, {
                documentInfo: {
                    AccessionNumber: accessionNumber,
                    Keyword: keyword,
                    EntireText: entireText,
                    tocMatches: true
                }
            })
            .then((response) => {
                if (response.data && typeof response.data === 'object') {
                    setSectionKeywordMatches(response.data.matches || []);
                    setTotalMatches(response.data.totalMatches || 0);
                } else {
                    console.warn("Unexpected response format for matches:", response.data);
                }
            })
            .catch((err) => {
                console.error("Failed to fetch section keyword matches:", err);
            });
    }, [accessionNumber, apiUrl, keyword, entireText]);


    useEffect(() => {
        if (!documentHtml) return;

        let hashPart = '';

        if (USE_HASH_ROUTER) {
            hashPart = window.location.hash.slice(1); // remove leading #
        } else {
            hashPart = location.hash ? location.hash.slice(1) : '';
        }

        if (hashPart) {
            const safeId = hashPart.split('?')[0].split('/').pop();
            const selector = `#${CSS.escape(safeId)}`;

            setTimeout(() => {
                const target = document.querySelector(selector);
                const rightPane = document.querySelector('.right-pane');

                if (target && rightPane) {
                    const targetTop = target.getBoundingClientRect().top;
                    const paneTop = rightPane.getBoundingClientRect().top;
                    const scrollOffset = targetTop - paneTop + rightPane.scrollTop;

                    rightPane.scrollTo({
                        top: scrollOffset,
                        behavior: 'smooth'
                    });
                }
            }, 100);
        }
    }, [documentHtml, location, USE_HASH_ROUTER]);

    const handleTocClick = (e) => {
        const target = e.target.closest('a[href^="#"]');
        if (!target) return;
        e.preventDefault();
        const id = target.getAttribute('href').slice(1); // Remove the '#'
        const element = document.getElementById(id);
        const rightPane = document.querySelector('.right-pane');
        if (element && rightPane) {
            const elementTop = element.getBoundingClientRect().top;
            const paneTop = rightPane.getBoundingClientRect().top;
            const offset = elementTop - paneTop + rightPane.scrollTop;
            rightPane.scrollTo({
                top: offset,
                behavior: 'smooth'
            });
        }
    };

    // Handle pane resizing
    useEffect(() => {
        const handleMouseMove = (e) => {
            if (!isDragging.current) return;
            document.body.classList.add('no-select');

            const container = containerRef.current;
            const containerLeft = container.getBoundingClientRect().left;
            const containerWidth = containerRef.current?.offsetWidth || 0;

            const newLeftWidth = e.pageX - containerLeft;

            // Collapse right pane if dragged near the right edge
            if (newLeftWidth >= containerWidth - MIN_WIDTH) {
                setLeftWidth(containerWidth); // Fully expand left
            } else {
                setLeftWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newLeftWidth)));
            }
        };


        const handleMouseUp = () => {
            isDragging.current = false;
            document.body.classList.remove('no-select');
        };

        const handleTouchMove = (e) => {
            if (!isDragging.current || e.touches.length === 0) return;

            const touch = e.touches[0];
            const container = containerRef.current;
            const containerLeft = container.getBoundingClientRect().left;
            const containerWidth = container.offsetWidth;

            const newLeftWidth = touch.pageX - containerLeft;

            if (newLeftWidth >= containerWidth - MIN_WIDTH) {
                setLeftWidth(containerWidth);
            } else {
                setLeftWidth(Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, newLeftWidth)));
            }
        };


        const handleTouchEnd = () => {
            isDragging.current = false;
            document.body.classList.remove('no-select');
        };

        // Mouse
        window.addEventListener('mousemove', handleMouseMove);
        window.addEventListener('mouseup', handleMouseUp);

        // Touch
        window.addEventListener('touchmove', handleTouchMove);
        window.addEventListener('touchend', handleTouchEnd);


        return () => {
            window.removeEventListener('mousemove', handleMouseMove);
            window.removeEventListener('mouseup', handleMouseUp);
            window.removeEventListener('touchmove', handleTouchMove);
            window.removeEventListener('touchend', handleTouchEnd);
            document.body.classList.remove('no-select');
        };
    }, []);
    useEffect(() => {
        if (!documentHtml || !(keyword || entireText)) return;

        const rightPane = document.querySelector('.right-pane');
        if (!rightPane) return;

        const parseSearchTerms = (query) => {
            const tokens = query.trim().split(/\s+/);
            const highlightTerms = [];

            tokens.forEach((token) => {
                if (/^(AND|OR|NOT)$/i.test(token)) return;
                if (token.startsWith('-')) return;

                // Only include terms that are NOT field-specific (no colon)
                if (!token.includes(':')) {
                    highlightTerms.push(token);
                }
            });

            return highlightTerms;
        };

        const highlightTerms = parseSearchTerms(keyword || entireText);
        const escapedTerms = highlightTerms.map(term => term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'));
        const regex = new RegExp(`\\b(${escapedTerms.join('|')})\\b`, 'gi');

        const walk = (node) => {
            if (node.nodeType === Node.TEXT_NODE) {
                const { textContent } = node;
                if (!textContent) return;

                const matches = textContent.match(regex);
                if (matches) {
                    const span = document.createElement('span');
                    span.innerHTML = textContent.replace(regex, (match) =>
                        `<span class="hitsection"><span class="subhit">${match}</span></span>`
                    );
                    node.replaceWith(...span.childNodes);
                }
            } else if (
                node.nodeType === Node.ELEMENT_NODE &&
                !['SCRIPT', 'STYLE'].includes(node.tagName) &&
                !node.classList.contains('hitsection') &&
                !node.classList.contains('subhit')
            ) {
                for (let child of Array.from(node.childNodes)) {
                    walk(child);
                }
            }
        };

        walk(rightPane);

        const matchedElements = Array.from(rightPane.querySelectorAll('.subhit'));

        matchedElements.forEach((el) => {
            el.replaceWith(el.cloneNode(true));
        });

        const updatedElements = Array.from(rightPane.querySelectorAll('.subhit'));
        updatedElements.forEach((el, index) => {
            el.addEventListener('click', () => {
                console.log("clicked", index);
                setCurrentMatchIndex(index);
                el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            });
        });

        setHighlightedElements(updatedElements);
        setCurrentMatchIndex(0);
    }, [documentHtml, keyword, entireText]);

    useEffect(() => {
        highlightedElements.forEach((el, i) => {
            el.classList.toggle('active-match', i === currentMatchIndex);
        });
    }, [highlightedElements, currentMatchIndex]);

    const scrollToMatch = (index) => {
        const el = document.querySelectorAll('.subhit')[index];
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'center' });
            setCurrentMatchIndex(index);
        }
    };

    return (
        <div className={`split-view-container ${isPrintView ? 'print-view-toggle' : ''}`} ref={containerRef}>
            {!isPrintView && (
                <div
                    className={`left-pane ${isRightCollapsed ? 'full-expanded' : leftWidth <= MIN_WIDTH ? 'collapsed' : ''}`}
                    style={{ width: `${leftWidth}px` }}
                >
                    <div className="toc">
                        {docTitle && (
                            <div className="doc-title-block">
                                <td className="viewdoc-td">
                                    <a
                                        href={window.location.href}
                                        className="doc-title"
                                    >
                                        {docTitle}
                                    </a>
                                </td>
                                <hr className="viewdoc-hr" />
                                {(keyword || entireText) && totalMatches > 0 && (
                                    <div className="keyword-summary">
                                        <p style={{ margin: '0.5rem 0' }}>
                                            {totalMatches} occurrence{totalMatches !== 1 ? 's' : ''} of "
                                            <strong>{keyword || entireText}</strong>"
                                            &nbsp;
                                            <button
                                                onClick={handleClearHits}
                                                style={{
                                                    marginLeft: '8px',
                                                    background: 'none',
                                                    border: 'none',
                                                    color: '#337ab7',
                                                    textDecoration: 'underline',
                                                    cursor: 'pointer',
                                                    fontSize: '0.9rem'
                                                }}
                                                onMouseEnter={(e) => e.target.style.color = '#23527c'}
                                                onMouseLeave={(e) => e.target.style.color = '#337ab7'}
                                            >
                                                Clear Hits
                                            </button>
                                        </p>
                                        {highlightedElements.length > 0 && (
                                            <div style={{ display: 'flex', alignItems: 'center', marginTop: '0.5rem' }}>
                                                <button
                                                    onClick={() => scrollToMatch(Math.max(0, currentMatchIndex - 1))}
                                                    disabled={currentMatchIndex === 0}
                                                    className="link-button"
                                                    style={{ marginRight: '8px' }}
                                                >
                                                    &larr;
                                                </button>
                                                <span>Match {currentMatchIndex + 1} of {highlightedElements.length}</span>
                                                <button
                                                    onClick={() => scrollToMatch(Math.min(highlightedElements.length - 1, currentMatchIndex + 1))}
                                                    disabled={currentMatchIndex === highlightedElements.length - 1}
                                                    className="link-button"
                                                    style={{ marginLeft: '8px' }}
                                                >
                                                    &rarr;
                                                </button>
                                            </div>
                                        )}

                                        <hr className="viewdoc-hr" />
                                    </div>

                                )}
                            </div>
                        )}

                        <h3>Table of Contents</h3>
                        <table className="toc-table">
                            <thead>
                                <tr>
                                    {totalMatches > 0 && <th>Matches</th>}
                                    <th>Section</th>
                                </tr>
                            </thead>
                            <tbody>
                                {parsedToc.map((item, index) => {
                                    const match = sectionKeywordMatches.find(m => m.title.trim().toLowerCase() === item.title.trim().toLowerCase());
                                    return (
                                        <tr key={index}>
                                            {totalMatches > 0 && <td>{match && match.matches > 0 ? match.matches : ''}</td>}
                                            <td>
                                                <a href={item.href} onClick={handleTocClick}>
                                                    {item.title}
                                                </a>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <hr className="viewdoc-hr" />
                        <table id="xmlSource" style={{ marginTop: '1rem' }}>
                            <tbody>
                                <tr>
                                    <td className="viewdoc-td">
                                        <span style={{ fontWeight: 'bold' }}>
                                            Download <button
                                                onClick={async (e) => {
                                                    e.preventDefault();

                                                    try {
                                                        const response = await fetch(apiUrl, {
                                                            method: 'POST',
                                                            headers: {
                                                                'Content-Type': 'application/json',
                                                            },
                                                            body: JSON.stringify({
                                                                documentInfo: {
                                                                    AccessionNumber: accessionNumber,
                                                                    Raw: true
                                                                }
                                                            })
                                                        });

                                                        const xmlText = await response.text();
                                                        const blob = new Blob([xmlText], { type: 'application/xml' });
                                                        const blobUrl = URL.createObjectURL(blob);
                                                        window.open(blobUrl, '_blank');
                                                    } catch (err) {
                                                        console.error("Failed to open XML:", err);
                                                    }
                                                }}
                                                style={{
                                                    fontWeight: 'bold',
                                                    textDecoration: 'underline',
                                                    fontSize: '100%',
                                                    color: '#337ab7',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                }}
                                                onMouseEnter={(e) => {
                                                    e.target.style.color = '#23527c';
                                                }}
                                                onMouseLeave={(e) => {
                                                    e.target.style.color = '#337ab7';
                                                }}
                                            >TEI markup</button> for this file.</span>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
            <div
                className={`divider ${leftWidth <= MIN_WIDTH ? 'edge left-collapsed' : ''} ${isRightCollapsed ? 'edge right-collapsed' : ''}`}
                onMouseDown={() => { isDragging.current = true; }}
                onTouchStart={() => { isDragging.current = true; }}
            />


            <div className={`right-pane ${isRightCollapsed ? 'collapsed' : leftWidth <= MIN_WIDTH ? 'full-expanded' : ''}`}>

                {error ? (
                    <p className="error">{error}</p>
                ) : documentHtml ? (
                    <div className="document-content teiContainer">
                        <div dangerouslySetInnerHTML={{ __html: documentHtml }} />
                    </div>
                ) : (
                    <p>Document Loading...</p>
                )}
            </div>
        </div>
    );
}

export default ViewDocument;
